import { Stack } from '@mui/material';
import * as React from 'react';

import { APPBAR_HEIGHT } from '@/components/architecture/Layout/AppBar/AppBar.constants';
import { ActionCard } from '@/components/layout/ActionCard/ActionCard';
import { zIndexes } from '@/styles/zIndexes';

/**
 * Controls how the empty state occupies vertical space so its messaging is
 * centered within the available white space rather than pinned near the top.
 *
 * - `'viewport'`: the empty state is the sole content of a scroll region that
 *   fills the viewport below the app bar. Its ancestor wrappers have `auto`
 *   height, so a percentage-height cascade can't fill that space; instead we
 *   give it a viewport-relative min-height. Use for full-page/tab data states.
 * - `'flex'`: the empty state fills the remaining space of a flex-column parent
 *   that already stretches to a known height. Use when other content (e.g.
 *   cards) sits above the empty state, so centering accounts for that content.
 *   Requires the parent chain to establish a height (a `flexGrow`/`height: 100%`
 *   flex column); the empty state stretches to fill whatever is left.
 *
 * Omit entirely in height-constrained containers (e.g. presentation slides),
 * where the default padded layout is expected.
 */
export type SmallTableEmptyStateFill = 'viewport' | 'flex';

export interface SmallTableEmptyStateProps {
  heading: string;
  description: string;
  fill?: SmallTableEmptyStateFill;
  // Optional icon rendered above the heading.
  icon?: React.ReactNode;
  // Optional call-to-action (e.g. a create button) rendered beneath the copy.
  action?: React.ReactNode;
}

// Offset for the `'viewport'` fill: accounts for the app bar plus a page
// header/tabs row that sits above the table body, so the min-height fills the
// remaining white space rather than the entire viewport.
const HEADER_ALLOWANCE = '110px';

function getFillStyles(fill: SmallTableEmptyStateProps['fill']) {
  if (fill === 'viewport') {
    return {
      flexGrow: 1,
      minHeight: `calc(100vh - ${APPBAR_HEIGHT} - ${HEADER_ALLOWANCE})`,
    };
  }
  if (fill === 'flex') {
    return { flexGrow: 1, alignSelf: 'stretch' };
  }
  return { py: 22 };
}

export function SmallTableEmptyState({
  heading,
  description,
  fill,
  icon,
  action,
}: SmallTableEmptyStateProps) {
  return (
    <Stack
      // Elevate the zIndex in the default (padded) layout because of some MUI
      // internals. Skip it for the `fill` modes: those are full-page/tab data
      // states rendered as the sole content, and the elevated layer would paint
      // over sibling overlays anchored nearby (e.g. a header coachmark popper),
      // swallowing their clicks.
      zIndex={fill ? undefined : zIndexes.CARD}
      sx={{
        width: '100%',
        alignContent: 'center',
        justifyContent: 'center',
        alignItems: 'center',
        p: 4,
        ...getFillStyles(fill),
      }}
    >
      <ActionCard
        icon={icon}
        heading={heading}
        variant="noCard"
        description={description}
        actions={action}
        sx={{ maxWidth: 400 }}
      />
    </Stack>
  );
}
